import { ReactElement, useEffect, useMemo, useRef, useState, } from 'react';
import styled from 'styled-components';

import { ComponentProps } from '@components/ComponentProps';
import { container, grid, GridContainer, mediaQueries } from '@theme/lib';
import "react-datepicker/dist/react-datepicker.css";
import GenericCard from '../../components/GenericCard';
import Hero, { CreateHeroProps } from '../../components/Hero';
import Filter from '../../components/MultipleSelectFilter';
import Pagination from '../../components/Pagination';

import { NewsListingFragment, ShortHeroParagraph } from '@/__generated__/graphql';
import PageMeta from "@components/PageMeta";
import StyledPills from '@components/Pills';
import WYSIWYG from "@components/WYSIWYG";
import { getTheme } from "@lib/config";
import { useMobile } from "@lib/hooks/useMobile";
import {
  applyFilters,
  changePage,
  formatFilters,
  getAppliedFilters,
  ListingGridContainer,
  removeFilterFromSelected,
  SelectedFilters,
  StyledCardWrapper,
  StyledFilterList,
  StyledFilterWrapper,
} from '@lib/listing';
import Link from "next/link";

const StyledNewsListing = styled.div`
  padding-bottom: 7.5rem;
  ${mediaQueries.lg} {
    padding-bottom: 12rem;
  }

  li {
    list-style: none;
  }

  .feature-card {
    margin-bottom: 2rem;

    ${mediaQueries.lg} {
      margin-bottom: 3rem;
      grid-column: 2 / span 8;
    }
  }

  .react-datepicker-popper {
    z-index: 9999;
  }
  .react-datepicker-wrapper {
    margin-bottom: 12px;
    ${mediaQueries.md} {
      margin-bottom: 0;
      margin-left: 0;
      margin-right: auto;
    }
  }
`;

const StyledCardList = styled(GridContainer).attrs({ as: 'ul' })``;

const StyledGenericCardWrapper = styled(StyledCardWrapper)`
  .generic-card {
    margin: auto;
    max-width: none;

    ${mediaQueries.md} {
      max-width: 100%;
    }

    ${mediaQueries.lg} {
      max-width: 590px;
    }
  }

  .generic-card__img-wrapper {
    border-radius: ${props => props.theme.config.buttonRadius};
  }
`;

const StyledNoResult = styled.div`
  ${grid}
  ${container}
  margin-top: 80px;
  margin-bottom: 80px;
  p {
    text-align: center;
    font-weight: 400;
    font-size: 18px;
    line-height: 145%;
    color: ${(props) => props.theme.colors.text};
    a {
      font-weight: 700;
    }

    grid-column: 1 / span 5;

    ${mediaQueries.md} {
      grid-column: 2 / span 3;
    }

    ${mediaQueries.lg} {
      grid-column: 2 / span 8;
    }
  }
`;

type NewsListingProps = NewsListingFragment & {
  pageUrl: string;
  type: string;
};

const DefaultBlogHeroProps = {
  __typename: 'ShortHeroParagraph',
  title: 'Our blogs',
  eyebrow: null,
  showAsCircle: false,
  backgroundImage: null,
  primaryCta: null,
  secondaryCta: null,
} as ShortHeroParagraph;

const DefaultNewsHeroProps = {
  __typename: 'ShortHeroParagraph',
  title: 'Our news',
  eyebrow: null,
  showAsCircle: false,
  backgroundImage: null,
  primaryCta: null,
  secondaryCta: null,
} as ShortHeroParagraph;

export default function NewsListing({
                                         type,
                                         content,
                                         hero,
                                         pageUrl,
                                         searchFilters,
                                         searchResults,
                                         metatags,
                                       }: ComponentProps<NewsListingProps>): ReactElement {
  const resultSection = useRef<HTMLDivElement|null>(null);
  const isMobile = useMobile(1200);
  const heroProps = hero ? CreateHeroProps(hero) : CreateHeroProps(type === 'news' ? DefaultNewsHeroProps : DefaultBlogHeroProps);
  // Applied filters are the filters currently applied in the url. Only recompute this value if searchFilters change
  const appliedFilters = useMemo(
    () =>
      searchFilters
        ? getAppliedFilters(pageUrl, searchFilters.map((searchFilter) => searchFilter.name))
        : {},
    [searchFilters, pageUrl]
  );

  // Selected filters are the filters the user has selected but not yet applied
  const [selectedFilters, setSelectedFilters] = useState(appliedFilters);
  const [showResult, setShowResult] = useState<boolean>(() => {
    if (searchResults && searchResults?.total && searchResults?.total > 0) {
      return true;
    }
    return false;
  });
  const [isMainSite, setIsMainSite] = useState(false);
  const [date, setDate] = useState<Date | null>(() => {
    if (pageUrl) {
      const url = new URL(pageUrl);
      const urlDate = url.searchParams.get('date');
      if (urlDate !== null) {
        return new Date(urlDate);
      }
    }
    return null;
  });

  const [filtersCleared, setFiltersCleared] = useState(() => false);
  const [pills, setPills] = useState(Object.entries(appliedFilters).map(([key, value], index) => [...value]).flat());

  useEffect(() => {
    setFiltersCleared(localStorage.getItem("filtersCleared") === 'true' ? true : false);

    if (getTheme() === 'zsl') {
      setIsMainSite(true);
    } else {
      setIsMainSite(false);
    }
  }, []);

  const onCheckboxChange = (filterType: string, value: string) => {
    setSelectedFilters((prev) => {
      // Make a deep copy of the previous selectedFilters state
      const newFilters: SelectedFilters = {};
      for (const key in prev) {
        newFilters[key] = new Set(prev[key]);
      }

      // Toggle the changed value
      if (newFilters[filterType].has(value)) {
        newFilters[filterType].delete(value);
      } else {
        newFilters[filterType].add(value);
      }

      return newFilters;
    });
  };

  // When the user close the dropdown for a filter type, reset the selected filters of that filter type to what's currently applied
  const resetSelection = (filterType: string) => {
    setSelectedFilters((prev) => {
      // Make a deep copy of the previous selectedFilters state
      const newFilters: SelectedFilters = {};
      for (const key in prev) {
        newFilters[key] = new Set(prev[key]);
      }

      // Reset the filters of this filter type
      newFilters[filterType] = new Set(appliedFilters[filterType]);
      return newFilters;
    });
  };

  const removeFilter = async (filter: string) => {
    localStorage.setItem("filtersCleared", "true");
    applyFilters(pageUrl, removeFilterFromSelected(selectedFilters, filter))
  }

  const formattedFilters = formatFilters({
    href: pageUrl,
    filters: searchFilters ?? [],
    selectedFilters,
    appliedFilters,
    onCheckboxChange,
    onApply: applyFilters,
    onClose: resetSelection,
    title: 'Filter by',
  });

  useEffect(() => {
    if (Object.keys(appliedFilters).length > 0 && [...appliedFilters[Object.keys(appliedFilters)[0]]].length > 0 || filtersCleared === true) {
      if (resultSection.current !== null ) {
        let headerOffset = isMobile ? 110 : 180;
        let elementPosition = resultSection.current.getBoundingClientRect().top;
        let offsetPosition = elementPosition +  window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth"
        });
      }
    }

    if (filtersCleared === true) {
      localStorage.removeItem('filtersCleared');
    }
  }, [appliedFilters, isMobile, filtersCleared]);

  return (
    <>
      <PageMeta
        metatags={metatags}
        breadcrumbs={[]}
        path={pageUrl}
      />
      <StyledNewsListing>
        {heroProps && <Hero {...heroProps} />}
        {content && <GridContainer><WYSIWYG id="news-content" {...content} /></GridContainer>}
        <ListingGridContainer ref={resultSection}>
          <StyledFilterWrapper>
            {formattedFilters.length === 1 && <Filter {...formattedFilters[0]} />}
            {formattedFilters.length > 1 && (
              <StyledFilterList>
                {formattedFilters.map((filter) => (
                  <li key={filter.title}>
                    <Filter {...filter} />
                  </li>
                ))}
              </StyledFilterList>
            )}
          </StyledFilterWrapper>
          {pills.length > 0 && <StyledPills items={pills} onClick={removeFilter} />}
        </ListingGridContainer>
        {(showResult && searchResults) && (
          <>
            {(searchResults.resultList && searchResults.resultList.length > 0) && (
              <StyledCardList className='news-listing__card-list'>
                {searchResults.resultList.map((result) => (
                  <StyledGenericCardWrapper key={result.id}>
                    <GenericCard
                      {...result}
                      image={{
                        ...result.teaserImage,
                        alt: result.teaserImage.altText,
                        url: result.teaserImage.url,
                      }}
                      created={result.created}
                      description={result.teaserDescription || ""}
                      eyebrow={result.teaserEyebrow ?? undefined}
                      label={undefined}
                    />
                  </StyledGenericCardWrapper>
                ))}
              </StyledCardList>
            )}
            <Pagination
              href={pageUrl}
              totalPages={
                searchResults.totalPages ?? Math.ceil(searchResults.total / searchResults.pageSize)
              }
              currentPage={Math.max(searchResults.page + 1, 1)}
              siblingCount={3}
              onPageChange={changePage}
            />
          </>
        )}
        {!showResult && (
          <StyledNoResult>
            <p>On no! No articles found. <Link href={type === 'news' ? '/news' : '/blogs'} aria-label="Reset your filter">Reset your filters</Link> to see everything we have going on.</p>
          </StyledNoResult>
        )}
      </StyledNewsListing>
    </>
  );
}
